import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

const FinancialReports: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [reports, setReports] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [showCompareModal, setShowCompareModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedReport, setSelectedReport] = useState<any>(null)
  const [comparisonData, setComparisonData] = useState<any>(null)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [reportType, setReportType] = useState('PROFIT_LOSS')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [comparePeriod1Start, setComparePeriod1Start] = useState('')
  const [comparePeriod1End, setComparePeriod1End] = useState('')
  const [comparePeriod2Start, setComparePeriod2Start] = useState('')
  const [comparePeriod2End, setComparePeriod2End] = useState('')
  const [compareReportType, setCompareReportType] = useState('PROFIT_LOSS')
  const [compareSelectedAccountIds, setCompareSelectedAccountIds] = useState<string[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [reportTemplates, setReportTemplates] = useState<any[]>([])
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false)
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    periodType: 'CUSTOM',
    periodOffset: 0,
    isDefault: false,
  })

  useEffect(() => {
    fetchReports()
    fetchReportTemplates()
  }, [])

  const fetchReportTemplates = async () => {
    try {
      const response = await axios.get(`/api/erp/report-templates?reportType=${reportType}`)
      setReportTemplates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching report templates:', error)
    }
  }

  useEffect(() => {
    if (showGenerateModal) {
      fetchReportTemplates()
    }
  }, [showGenerateModal, reportType])

  useEffect(() => {
    if (showGenerateModal && (reportType === 'GENERAL_LEDGER' || reportType === 'ACCOUNT_STATEMENT')) {
      fetchAccounts()
    }
  }, [showGenerateModal, reportType])

  useEffect(() => {
    if (showCompareModal && (compareReportType === 'GENERAL_LEDGER' || compareReportType === 'ACCOUNT_STATEMENT')) {
      fetchAccounts()
    }
  }, [showCompareModal, compareReportType])

  const fetchReports = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/reports')
      setReports(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching reports:', error)
      toast.error('Failed to load reports')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      setIsLoadingAccounts(true)
      const response = await axios.get('/api/erp/accounts')
      setAccounts(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching accounts:', error)
      toast.error('Failed to load accounts')
    } finally {
      setIsLoadingAccounts(false)
    }
  }

  const handleGenerate = async () => {
    // Validate required fields
    if (!periodStart || !periodEnd) {
      toast.error('Please select both start and end dates')
      return
    }

    // Validate account selection for General Ledger and Account Statement
    if ((reportType === 'GENERAL_LEDGER' || reportType === 'ACCOUNT_STATEMENT') && selectedAccountIds.length === 0) {
      toast.error('Please select at least one account')
      return
    }

    try {
      const payload: any = {
        reportType,
        periodStart: new Date(periodStart),
        periodEnd: new Date(periodEnd),
      }

      // Add filters for reports that need account selection
      if (reportType === 'GENERAL_LEDGER' || reportType === 'ACCOUNT_STATEMENT') {
        payload.filters = {
          accountIds: selectedAccountIds,
        }
      }

      const response = await axios.post('/api/erp/reports/generate', payload)
      toast.success('Report generated successfully')
      setShowGenerateModal(false)
      setSelectedAccountIds([])
      fetchReports()
      // Auto-open the newly generated report
      if (response.data.data) {
        setSelectedReport(response.data.data)
        setShowViewModal(true)
      }
    } catch (error: any) {
      console.error('Error generating report:', error)
      toast.error(error.response?.data?.error || 'Failed to generate report')
    }
  }

  const handleGenerateFromTemplate = async (templateId: string) => {
    try {
      const response = await axios.post(`/api/erp/reports/generate-from-template/${templateId}`)
      toast.success('Report generated from template successfully')
      setShowGenerateModal(false)
      fetchReports()
      if (response.data.data) {
        setSelectedReport(response.data.data)
        setShowViewModal(true)
      }
    } catch (error: any) {
      console.error('Error generating report from template:', error)
      toast.error(error.response?.data?.error || 'Failed to generate report from template')
    }
  }

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const payload: any = {
        ...templateFormData,
        reportType,
        filters: (reportType === 'GENERAL_LEDGER' || reportType === 'ACCOUNT_STATEMENT') && selectedAccountIds.length > 0
          ? { accountIds: selectedAccountIds }
          : {},
      }

      await axios.post('/api/erp/report-templates', payload)
      toast.success('Report template saved successfully')
      setShowSaveTemplateModal(false)
      setTemplateFormData({
        name: '',
        description: '',
        periodType: 'CUSTOM',
        periodOffset: 0,
        isDefault: false,
      })
      fetchReportTemplates()
    } catch (error: any) {
      console.error('Error saving template:', error)
      toast.error(error.response?.data?.error || 'Failed to save template')
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return
    }
    try {
      await axios.delete(`/api/erp/report-templates/${templateId}`)
      toast.success('Template deleted successfully')
      fetchReportTemplates()
    } catch (error: any) {
      console.error('Error deleting template:', error)
      toast.error(error.response?.data?.error || 'Failed to delete template')
    }
  }

  const handleCompare = async () => {
    // Validate required fields
    if (!comparePeriod1Start || !comparePeriod1End || !comparePeriod2Start || !comparePeriod2End) {
      toast.error('Please select both periods for comparison')
      return
    }

    // Validate account selection for General Ledger and Account Statement
    if ((compareReportType === 'GENERAL_LEDGER' || compareReportType === 'ACCOUNT_STATEMENT') && compareSelectedAccountIds.length === 0) {
      toast.error('Please select at least one account')
      return
    }

    try {
      setIsLoadingReport(true)
      const payload: any = {
        reportType: compareReportType,
        period1Start: new Date(comparePeriod1Start),
        period1End: new Date(comparePeriod1End),
        period2Start: new Date(comparePeriod2Start),
        period2End: new Date(comparePeriod2End),
      }

      // Add filters for reports that need account selection
      if (compareReportType === 'GENERAL_LEDGER' || compareReportType === 'ACCOUNT_STATEMENT') {
        payload.filters = {
          accountIds: compareSelectedAccountIds,
        }
      }

      const response = await axios.post('/api/erp/reports/compare', payload)
      setComparisonData(response.data.data)
      setShowCompareModal(false)
      setShowViewModal(true)
      setSelectedReport(null) // Clear single report view
    } catch (error: any) {
      console.error('Error comparing reports:', error)
      toast.error(error.response?.data?.error || 'Failed to compare reports')
    } finally {
      setIsLoadingReport(false)
    }
  }

  const handleViewReport = async (reportId: string) => {
    setIsLoadingReport(true)
    try {
      const response = await axios.get(`/api/erp/reports/${reportId}`)
      setSelectedReport(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching report:', error)
      toast.error('Failed to load report details')
    } finally {
      setIsLoadingReport(false)
    }
  }

  const renderComparisonData = (comparison: any) => {
    if (!comparison || !comparison.comparison) return <p className="text-gray-500">No comparison data available</p>

    const comp = comparison.comparison
    const reportType = comparison.reportType

    switch (reportType) {
      case 'PROFIT_LOSS':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4 font-semibold border-b pb-2">
              <div>Item</div>
              <div className="text-right">Period 1</div>
              <div className="text-right">Period 2</div>
              <div className="text-right">Variance</div>
            </div>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-4 gap-4 py-2">
                <div>Revenue</div>
                <div className="text-right">{comp.revenue.period1.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className="text-right">{comp.revenue.period2.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className={`text-right font-semibold ${comp.revenue.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comp.revenue.variance >= 0 ? '+' : ''}{comp.revenue.variance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ({comp.revenue.variancePercent.toFixed(1)}%)
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 py-2">
                <div>Expenses</div>
                <div className="text-right">{comp.expenses.period1.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className="text-right">{comp.expenses.period2.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className={`text-right font-semibold ${comp.expenses.variance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comp.expenses.variance >= 0 ? '+' : ''}{comp.expenses.variance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ({comp.expenses.variancePercent.toFixed(1)}%)
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 py-2 border-t pt-2 font-bold">
                <div>Net Income</div>
                <div className="text-right">{comp.netIncome.period1.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className="text-right">{comp.netIncome.period2.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className={`text-right ${comp.netIncome.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comp.netIncome.variance >= 0 ? '+' : ''}{comp.netIncome.variance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ({comp.netIncome.variancePercent.toFixed(1)}%)
                </div>
              </div>
            </div>
          </div>
        )

      case 'BALANCE_SHEET':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4 font-semibold border-b pb-2">
              <div>Item</div>
              <div className="text-right">Period 1</div>
              <div className="text-right">Period 2</div>
              <div className="text-right">Variance</div>
            </div>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-4 gap-4 py-2">
                <div>Assets</div>
                <div className="text-right">{comp.assets.period1.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className="text-right">{comp.assets.period2.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className={`text-right font-semibold ${comp.assets.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comp.assets.variance >= 0 ? '+' : ''}{comp.assets.variance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ({comp.assets.variancePercent.toFixed(1)}%)
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 py-2">
                <div>Liabilities</div>
                <div className="text-right">{comp.liabilities.period1.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className="text-right">{comp.liabilities.period2.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className={`text-right font-semibold ${comp.liabilities.variance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comp.liabilities.variance >= 0 ? '+' : ''}{comp.liabilities.variance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ({comp.liabilities.variancePercent.toFixed(1)}%)
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 py-2">
                <div>Equity</div>
                <div className="text-right">{comp.equity.period1.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className="text-right">{comp.equity.period2.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>
                <div className={`text-right font-semibold ${comp.equity.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comp.equity.variance >= 0 ? '+' : ''}{comp.equity.variance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ({comp.equity.variancePercent.toFixed(1)}%)
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-yellow-800">Comparison view for {reportType.replace(/_/g, ' ')} is available but detailed breakdown is not yet implemented.</p>
            <p className="text-sm text-yellow-700 mt-2">Use the individual report views to see detailed data for each period.</p>
          </div>
        )
    }
  }

  const renderReportData = (report: any) => {
    if (!report.data) return <p className="text-gray-500">No data available</p>

    const data = report.data

    switch (report.reportType) {
      case 'PROFIT_LOSS':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Income</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.income?.items?.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                      <td className="px-4 py-2 text-right">{item.amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-2">Total Income</td>
                    <td className="px-4 py-2 text-right">{data.income?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Expenses</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.expenses?.items?.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                      <td className="px-4 py-2 text-right">{item.amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-2">Total Expenses</td>
                    <td className="px-4 py-2 text-right">{data.expenses?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-blue-50 p-4 rounded border-2 border-blue-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Net Income (Loss)</span>
                <span className={`text-xl font-bold ${data.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {data.netIncome?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                </span>
              </div>
            </div>
          </div>
        )

      case 'BALANCE_SHEET':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Assets</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.assets?.items?.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                      <td className="px-4 py-2 text-right">{item.balance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-2">Total Assets</td>
                    <td className="px-4 py-2 text-right">{data.assets?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Liabilities</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.liabilities?.items?.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                      <td className="px-4 py-2 text-right">{item.balance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-2">Total Liabilities</td>
                    <td className="px-4 py-2 text-right">{data.liabilities?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Equity</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.equity?.items?.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                      <td className="px-4 py-2 text-right">{item.balance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-2">Total Equity</td>
                    <td className="px-4 py-2 text-right">{data.equity?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-blue-50 p-4 rounded border-2 border-blue-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Total Assets = Liabilities + Equity</span>
                <span className="text-xl font-bold">
                  {data.assets?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} = 
                  {' '}{data.liabilities?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} + 
                  {' '}{data.equity?.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                </span>
              </div>
            </div>
          </div>
        )

      case 'TRIAL_BALANCE':
        return (
          <div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.accounts?.map((item: any, idx: number) => (
                  <tr key={idx}>
                    <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                    <td className="px-4 py-2 text-right">{item.debit > 0 ? item.debit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}</td>
                    <td className="px-4 py-2 text-right">{item.credit > 0 ? item.credit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right">{data.totalDebit?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totalCredit?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )

      case 'AR_AGING':
        return (
          <div>
            <p className="text-sm text-gray-600 mb-4">As of: {new Date(data.asOfDate).toLocaleDateString()}</p>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">1-30 Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">31-60 Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">61-90 Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">90+ Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.invoices?.map((item: any, idx: number) => {
                  const buckets = {
                    current: item.ageBucket === 'current' ? item.outstandingAmount : 0,
                    over0: item.ageBucket === 'over0' ? item.outstandingAmount : 0,
                    over30: item.ageBucket === 'over30' ? item.outstandingAmount : 0,
                    over60: item.ageBucket === 'over60' ? item.outstandingAmount : 0,
                    over90: item.ageBucket === 'over90' ? item.outstandingAmount : 0,
                  }
                  return (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.invoiceNumber}</td>
                      <td className="px-4 py-2">{item.clientName}</td>
                      <td className="px-4 py-2">{new Date(item.dueDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-right">{buckets.current.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over0.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over30.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over60.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over90.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right font-semibold">{item.outstandingAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  )
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={3} className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right">{data.totals?.current?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over0?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over30?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over60?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over90?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.grandTotal?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )

      case 'AP_AGING':
        return (
          <div>
            <p className="text-sm text-gray-600 mb-4">As of: {new Date(data.asOfDate).toLocaleDateString()}</p>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expense #</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">1-30 Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">31-60 Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">61-90 Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">90+ Days</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.expenses?.map((item: any, idx: number) => {
                  const buckets = {
                    current: item.ageBucket === 'current' ? item.outstandingAmount : 0,
                    over0: item.ageBucket === 'over0' ? item.outstandingAmount : 0,
                    over30: item.ageBucket === 'over30' ? item.outstandingAmount : 0,
                    over60: item.ageBucket === 'over60' ? item.outstandingAmount : 0,
                    over90: item.ageBucket === 'over90' ? item.outstandingAmount : 0,
                  }
                  return (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.expenseNumber}</td>
                      <td className="px-4 py-2">{item.vendor || '-'}</td>
                      <td className="px-4 py-2">{new Date(item.expenseDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-right">{buckets.current.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over0.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over30.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over60.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right">{buckets.over90.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                      <td className="px-4 py-2 text-right font-semibold">{item.outstandingAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                    </tr>
                  )
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={3} className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right">{data.totals?.current?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over0?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over30?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over60?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.totals?.over90?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{data.grandTotal?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )

      case 'GENERAL_LEDGER':
        return (
          <div className="space-y-6">
            {data.accounts?.map((account: any, accountIdx: number) => (
              <div key={accountIdx} className="border border-gray-200 rounded-lg p-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold">{account.accountCode} - {account.accountName}</h3>
                  <div className="text-sm text-gray-600 mt-1">
                    <span>Opening Balance: {account.openingBalance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
                    <span className="ml-4">Closing Balance: {account.closingBalance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
                  </div>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Voucher</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr className="bg-gray-100">
                      <td colSpan={5} className="px-4 py-2 font-semibold">Opening Balance</td>
                      <td className="px-4 py-2 text-right font-semibold">
                        {account.openingBalance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                      </td>
                    </tr>
                    {account.entries?.map((entry: any, entryIdx: number) => {
                      const runningBalance = account.openingBalance + 
                        account.entries.slice(0, entryIdx + 1).reduce((sum: number, e: any) => sum + e.debit - e.credit, 0);
                      return (
                        <tr key={entryIdx}>
                          <td className="px-4 py-2 text-sm">{new Date(entry.date).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm">{entry.description || '-'}</td>
                          <td className="px-4 py-2 text-sm">{entry.voucherType} {entry.voucherNo}</td>
                          <td className="px-4 py-2 text-sm text-right">
                            {entry.debit > 0 ? entry.debit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-right">
                            {entry.credit > 0 ? entry.credit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">
                            {runningBalance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-gray-100 font-semibold">
                      <td colSpan={5} className="px-4 py-2">Closing Balance</td>
                      <td className="px-4 py-2 text-right">
                        {account.closingBalance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )

      case 'ACCOUNT_STATEMENT':
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">{data.account?.code} - {data.account?.name}</h3>
              <div className="text-sm text-gray-600 mt-1">
                <span>Opening Balance: {data.openingBalance?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
                <span className="ml-4">Closing Balance: {data.closingBalance?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Voucher</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="bg-gray-100">
                  <td colSpan={5} className="px-4 py-2 font-semibold">Opening Balance</td>
                  <td className="px-4 py-2 text-right font-semibold">
                    {data.openingBalance?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </td>
                </tr>
                {data.transactions?.map((entry: any, entryIdx: number) => (
                  <tr key={entryIdx}>
                    <td className="px-4 py-2 text-sm">{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-sm">{entry.description || '-'}</td>
                    <td className="px-4 py-2 text-sm">{entry.voucherType} {entry.voucherNo}</td>
                    <td className="px-4 py-2 text-sm text-right">
                      {entry.debit > 0 ? entry.debit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right">
                      {entry.credit > 0 ? entry.credit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-semibold">
                      {entry.balance?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={5} className="px-4 py-2">Closing Balance</td>
                  <td className="px-4 py-2 text-right">
                    {data.closingBalance?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )

      case 'COST_CENTER_REPORT':
        return (
          <div className="space-y-6">
            {data.costCenters?.map((costCenter: any, idx: number) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3">{costCenter.name}</h3>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {costCenter.accounts?.map((item: any, itemIdx: number) => (
                      <tr key={itemIdx}>
                        <td className="px-4 py-2">{item.accountCode} - {item.accountName}</td>
                        <td className="px-4 py-2 text-right">
                          {item.amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-2">Total</td>
                      <td className="px-4 py-2 text-right">
                        {costCenter.total?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )

      default:
        return (
          <div>
            <p className="text-gray-500 mb-2">Report data (raw format):</p>
            <pre className="bg-gray-50 p-4 rounded overflow-auto text-xs max-h-96">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )
    }
  }

  if (permissionsLoading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading permissions...</div>
    )
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view financial reports.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
            <PageHelp pageIdentifier="financial-reports" title="Financial Reports" />
          </div>
          <p className="text-gray-600 mt-1">Generate and view financial reports</p>
        </div>
        {hasPermission('erp_financial_report', 'generate') && (
          <div className="flex gap-3">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Generate Report
            </button>
            <button
              onClick={() => {
                // Set default periods (current month vs previous month)
                const now = new Date()
                const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
                const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0)
                
                setComparePeriod1Start(currentMonthStart.toISOString().split('T')[0])
                setComparePeriod1End(currentMonthEnd.toISOString().split('T')[0])
                setComparePeriod2Start(prevMonthStart.toISOString().split('T')[0])
                setComparePeriod2End(prevMonthEnd.toISOString().split('T')[0])
                setCompareReportType('PROFIT_LOSS')
                setCompareSelectedAccountIds([])
                setShowCompareModal(true)
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Compare Reports
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period Start</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period End</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewReport(report.id)}>
                  <td className="px-4 py-2">{report.reportType.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-2">{new Date(report.periodStart).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{new Date(report.periodEnd).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{new Date(report.generatedAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewReport(report.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isLoadingReport}
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleExportReport(report.id, 'pdf')}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                        title="Export as PDF"
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => handleExportReport(report.id, 'csv')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Export as CSV"
                      >
                        CSV
                      </button>
                      <button
                        onClick={() => handleExportReport(report.id, 'excel')}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        title="Export as Excel"
                      >
                        Excel
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Generate Report</h2>
              <div className="flex gap-2">
                {hasPermission('erp_financial_report', 'write') && (
                  <button
                    onClick={() => {
                      setTemplateFormData({
                        name: '',
                        description: '',
                        periodType: 'CUSTOM',
                        periodOffset: 0,
                        isDefault: false,
                      })
                      setShowSaveTemplateModal(true)
                    }}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    Save Template
                  </button>
                )}
                <button
                  onClick={() => setShowTemplateModal(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Load Template
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {reportTemplates.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quick Templates</label>
                  <div className="flex flex-wrap gap-2">
                    {reportTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleGenerateFromTemplate(template.id)}
                        className={`px-3 py-1 text-sm rounded ${
                          template.isDefault
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-white border border-blue-300 text-blue-700 hover:bg-blue-50'
                        }`}
                      >
                        {template.name}
                        {template.isDefault && ' (Default)'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="PROFIT_LOSS">Profit & Loss</option>
                  <option value="BALANCE_SHEET">Balance Sheet</option>
                  <option value="TRIAL_BALANCE">Trial Balance</option>
                  <option value="GENERAL_LEDGER">General Ledger</option>
                  <option value="AR_AGING">AR Aging</option>
                  <option value="AP_AGING">AP Aging</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period Start</label>
                <input
                  type="date"
                  required
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period End</label>
                <input
                  type="date"
                  required
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              {(reportType === 'GENERAL_LEDGER' || reportType === 'ACCOUNT_STATEMENT') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Accounts {selectedAccountIds.length > 0 && `(${selectedAccountIds.length} selected)`}
                  </label>
                  {isLoadingAccounts ? (
                    <div className="text-sm text-gray-500">Loading accounts...</div>
                  ) : (
                    <div className="border border-gray-300 rounded px-3 py-2 max-h-48 overflow-y-auto">
                      {accounts.length === 0 ? (
                        <div className="text-sm text-gray-500">No accounts available</div>
                      ) : (
                        accounts
                          .filter((acc: any) => !acc.isGroup && acc.isActive)
                          .map((account: any) => (
                            <label key={account.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedAccountIds.includes(account.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAccountIds([...selectedAccountIds, account.id])
                                  } else {
                                    setSelectedAccountIds(selectedAccountIds.filter((id) => id !== account.id))
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm">
                                {account.code} - {account.name}
                              </span>
                            </label>
                          ))
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select one or more accounts to include in the report
                  </p>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compare Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Compare Reports</h2>
              <button
                onClick={() => setShowCompareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Report Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={compareReportType}
                  onChange={(e) => {
                    setCompareReportType(e.target.value)
                    setCompareSelectedAccountIds([])
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="PROFIT_LOSS">Profit & Loss</option>
                  <option value="BALANCE_SHEET">Balance Sheet</option>
                  <option value="TRIAL_BALANCE">Trial Balance</option>
                  <option value="GENERAL_LEDGER">General Ledger</option>
                  <option value="ACCOUNT_STATEMENT">Account Statement</option>
                  <option value="COST_CENTER_REPORT">Cost Center Report</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold mb-2">Period 1</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={comparePeriod1Start}
                        onChange={(e) => setComparePeriod1Start(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={comparePeriod1End}
                        onChange={(e) => setComparePeriod1End(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-2">Period 2</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={comparePeriod2Start}
                        onChange={(e) => setComparePeriod2Start(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={comparePeriod2End}
                        onChange={(e) => setComparePeriod2End(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {(compareReportType === 'GENERAL_LEDGER' || compareReportType === 'ACCOUNT_STATEMENT') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accounts <span className="text-red-500">*</span>
                  </label>
                  {isLoadingAccounts ? (
                    <p className="text-sm text-gray-500">Loading accounts...</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded p-2">
                      {accounts.filter((acc: any) => !acc.isGroup && acc.isActive).map((account: any) => (
                        <label key={account.id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            checked={compareSelectedAccountIds.includes(account.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCompareSelectedAccountIds([...compareSelectedAccountIds, account.id])
                              } else {
                                setCompareSelectedAccountIds(compareSelectedAccountIds.filter(id => id !== account.id))
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{account.code} - {account.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCompareModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompare}
                  disabled={isLoadingReport}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {isLoadingReport ? 'Comparing...' : 'Compare'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {showViewModal && (selectedReport || comparisonData) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {selectedReport.reportType.replace(/_/g, ' ')} Report
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedReport(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 text-sm text-gray-600">
              <p><strong>Period:</strong> {new Date(selectedReport.periodStart).toLocaleDateString()} - {new Date(selectedReport.periodEnd).toLocaleDateString()}</p>
              <p><strong>Generated:</strong> {new Date(selectedReport.generatedAt).toLocaleString()}</p>
            </div>

            <div className="mt-4">
              {renderReportData(selectedReport)}
            </div>

            {selectedReport && (
              <div className="mt-6 flex justify-between items-center">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleExportReport(selectedReport.id, 'pdf')}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    Export PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportReport(selectedReport.id, 'csv')}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                  >
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportReport(selectedReport.id, 'excel')}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    Export Excel
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedReport(null)
                    setComparisonData(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            )}
            {comparisonData && (
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedReport(null)
                    setComparisonData(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default FinancialReports
